import { Sparkles, Upload, Lock, FileText, Eye } from 'lucide-react';
import PlanGate from '../../../../../../../components/gating/PlanGate';
import { toast } from 'react-hot-toast';

const EnrichmentHeader = ({
  formData,
  setIsEnrichModalOpen,
  isVoucherLocked,
  navigate
}) => {
  return (
    <div className="space-y-4">
      {formData.quality_analysis?.is_low_quality && (
        <div className="bg-amber-50/70 border border-amber-200/80 rounded-3xl p-5 md:p-6 animate-in slide-in-from-top-4 duration-500 font-sans shadow-xs">
          <div className="flex flex-col md:flex-row items-start gap-4">
            <div className="w-10 h-10 bg-amber-100/80 text-amber-700 rounded-2xl flex items-center justify-center shrink-0 border border-amber-200/50">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                <h3 className="text-base font-bold text-amber-900">Low Quality Documents Detected</h3>
                <button
                  type="button"
                  onClick={() => setIsEnrichModalOpen(true)}
                  className="w-full sm:w-auto px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-xs transition-all cursor-pointer shrink-0"
                >
                  Fix &amp; Re-extract
                </button>
              </div>
              <p className="text-xs sm:text-sm text-amber-800/90 font-normal leading-relaxed">
                Some details could not be read clearly from your documents. Please review the information carefully or re-upload clearer photos.
              </p>

              {formData.quality_analysis.reasons?.length > 0 && (
                <div className="mt-3.5 space-y-1.5 bg-amber-100/40 p-3 rounded-2xl border border-amber-200/60">
                  {formData.quality_analysis.reasons.map((reason, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-amber-900 font-normal leading-snug">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0 mt-1.5" />
                      <span>{reason}</span>
                    </div>
                  ))}
                </div>
              )}

              {formData.quality_analysis.low_quality_links?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3">
                  {formData.quality_analysis.low_quality_links.map((link, idx) => (
                    <div key={idx} className="group relative">
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-white shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 bg-white"
                      >
                        {link.toLowerCase().includes('.pdf') ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-rose-50 text-rose-500">
                            <FileText size={24} />
                            <span className="text-[8px] font-black uppercase mt-1">PDF</span>
                          </div>
                        ) : (
                          <img
                            src={link}
                            alt={`Low Quality ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/100?text=File';
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye size={20} className="text-white" />
                        </div>
                      </a>
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                        {idx + 1}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document Enrichment Banner */}
      <div className="bg-white border border-slate-200 rounded-[2rem] p-4 md:px-6 md:py-4 mb-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-[1.25rem] flex items-center justify-center shrink-0 shadow-inner">
            <Sparkles size={24} />
          </div>
          <div>
            <h3 className="text-xs sm:text-sm font-medium text-slate-500">Add more documents to extract more details</h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <PlanGate
            featureKey="voucher-creation"
            featureName="Voucher Creation"
            tooltipText="Subscription required to create vouchers."
            showLockIcon={false}
            wrapperClassName="w-full md:w-auto"
          >
            <button
              type="button"
              onClick={() => {
                if (isVoucherLocked) {
                  navigate('/subscription');
                  toast.error('Active subscription required to create vouchers. Upgrade to unlock.');
                } else {
                  setIsEnrichModalOpen(true);
                }
              }}
              className="flex-1 md:flex-initial flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-bold text-sm shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all active:scale-95 shrink-0 cursor-pointer"
            >
              <Upload size={16} strokeWidth={2.5} />
              <span>Add Document &amp; Extract</span>
              {isVoucherLocked && <Lock size={14} className="ml-1 text-white/90 shrink-0" />}
            </button>
          </PlanGate>
        </div>
      </div>
    </div>
  );
};

export default EnrichmentHeader;
